import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Alert, Button, Container, Fade, Form, Spinner, Table} from 'react-bootstrap';
import CommonMetaTag from './common-meta-tag';
import * as api from '../api';
import {useTranslation} from 'react-i18next';

const emptyForm = {
  id: null,
  sport: 'pickleball',
  provider: 'pbvision',
  enabled: true,
  pricePerMinuteCents: 100,
  providerCostPerMinuteCents: 0,
  maxDurationSeconds: 1800,
  minDurationSeconds: 1,
  roundingMode: 'ceil_minute',
  userInstructions: ''
};

const safeText = value => (value == null ? '' : String(value));

const normalizeConfig = config => ({
  id: config.id || null,
  sport: safeText(config.sport || 'pickleball'),
  provider: safeText(config.provider || 'pbvision'),
  enabled: config.enabled !== false,
  pricePerMinuteCents: Number(config.pricePerMinuteCents) || 0,
  providerCostPerMinuteCents: Number(config.providerCostPerMinuteCents) || 0,
  maxDurationSeconds: Number(config.maxDurationSeconds) || 1800,
  minDurationSeconds: Number(config.minDurationSeconds) || 1,
  roundingMode: safeText(config.roundingMode || 'ceil_minute'),
  userInstructions: safeText(config.userInstructions)
});

const centsToEur = cents => `${((Number(cents) || 0) / 100).toFixed(2)} EUR`;
const titleTextStyle = {color: '#ffffff'};
const panelStyle = {background: '#0c213d', color: '#ffffff', padding: 24, borderRadius: 4};
const tableStyle = {background: '#0c213d', color: '#ffffff'};
const whiteTextStyle = {color: '#ffffff'};
const labelTextStyle = {color: '#ffffff', fontWeight: 600};

const AdminAi = ({token}) => {
  const {t} = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadConfigs = () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setMessage(null);
    api.adminGetVideoAnalysisConfigs(token).then(result => {
      setLoading(false);
      if (result.status >= 200 && result.status < 300 && Array.isArray(result.json)) {
        setConfigs(result.json.map(normalizeConfig));
        return;
      }
      setMessage({variant: 'danger', text: t('ADMIN_AI_LOAD_ERROR')});
    }).catch(() => {
      setLoading(false);
      setMessage({variant: 'danger', text: t('ADMIN_AI_LOAD_ERROR')});
    });
  };

    useEffect(loadConfigs, [token, t]);

  const updateField = field => event => {
    const {type, checked, value} = event.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setForm(current => ({...current, [field]: nextValue}));
  };

  const editConfig = config => {
    setForm(normalizeConfig(config));
    window.scrollTo(0, 0);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setMessage(null);
  };

  const submit = event => {
    event.preventDefault();
    if (!token) {
      setMessage({variant: 'danger', text: t('SESSION_EXPIRED_RELOGIN')});
      return;
    }
    setSaving(true);
    setMessage(null);
    const payload = {
      ...form,
      pricePerMinuteCents: Number(form.pricePerMinuteCents) || 0,
      providerCostPerMinuteCents: Number(form.providerCostPerMinuteCents) || 0,
      maxDurationSeconds: Number(form.maxDurationSeconds) || 1800,
      minDurationSeconds: Number(form.minDurationSeconds) || 1,
    };
    const request = form.id
      ? api.adminUpdateVideoAnalysisConfig(form.id, payload, token)
      : api.adminCreateVideoAnalysisConfig(payload, token);
    request.then(result => {
      setSaving(false);
      if (result.status >= 200 && result.status < 300) {
        setMessage({variant: 'success', text: t('ADMIN_AI_SAVED')});
        setForm(emptyForm);
        loadConfigs();
        return;
      }
      setMessage({variant: 'danger', text: t('SAVE_ERROR_WITH_DETAIL', {error: result.json && result.json.error ? result.json.error : result.status})});
    }).catch(() => {
      setSaving(false);
      setMessage({variant: 'danger', text: t('SAVE_REQUEST_FAILED')});
    });
  };

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="bg_white" style={{transition: 'opacity 0.30s linear'}}>
        <Container fluid className="presentation-container">
          <div className="page-header header-filter clear-filter dashboard_hero_rp" data-parallax="true">
            <div className="dashboard_hero_overlay_rp"/>
          </div>
          <div className="main main_raised_rp" style={{minHeight: 800}}>
            <div className="section">
              <div className="container admin-ai-page">
                <h4 className="section_title_neg_rp" style={titleTextStyle}>{t('ADMIN_AI_TITLE')}</h4>
                <p style={titleTextStyle}>{t('ADMIN_AI_INTRO')}</p>
                {message && <Alert variant={message.variant}>{message.text}</Alert>}
                <Form onSubmit={submit} className="mb-5" style={panelStyle}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{gap: 10}}>
                    <h5 style={{...whiteTextStyle, marginBottom: 0}}>
                      {form.id ? t('ADMIN_AI_EDIT_CONFIG', {id: form.id}) : t('ADMIN_AI_NEW_CONFIG')}
                    </h5>
                    <Button type="submit" className="btn btn_red_rp" disabled={saving || !token}>
                      {saving ? <Spinner animation="border" size="sm" className="mr-2"/> : null}
                      {t('SAVE')}
                    </Button>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('SPORT')}</Form.Label>
                        <Form.Control required value={form.sport} onChange={updateField('sport')} placeholder="pickleball"/>
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('PROVIDER')}</Form.Label>
                        <Form.Control as="select" value={form.provider} onChange={updateField('provider')}>
                          <option value="pbvision">PBVision</option>
                        </Form.Control>
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('STATE')}</Form.Label>
                        <Form.Check type="checkbox" label={t('ENABLED')} checked={form.enabled} onChange={updateField('enabled')} style={whiteTextStyle}/>
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-3">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('ADMIN_AI_USER_PRICE_CENTS')}</Form.Label>
                        <Form.Control required type="number" min="0" value={form.pricePerMinuteCents} onChange={updateField('pricePerMinuteCents')}/>
                        <small style={whiteTextStyle}>{centsToEur(form.pricePerMinuteCents)} / min</small>
                      </Form.Group>
                    </div>
                    <div className="col-md-3">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('ADMIN_AI_PROVIDER_COST_CENTS')}</Form.Label>
                        <Form.Control required type="number" min="0" value={form.providerCostPerMinuteCents} onChange={updateField('providerCostPerMinuteCents')}/>
                        <small style={whiteTextStyle}>{centsToEur(form.providerCostPerMinuteCents)} / min</small>
                      </Form.Group>
                    </div>
                    <div className="col-md-3">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('ADMIN_AI_MAX_DURATION_SECONDS')}</Form.Label>
                        <Form.Control required type="number" min="1" max="1800" value={form.maxDurationSeconds} onChange={updateField('maxDurationSeconds')}/>
                      </Form.Group>
                    </div>
                    <div className="col-md-3">
                      <Form.Group>
                        <Form.Label style={labelTextStyle}>{t('ADMIN_AI_MIN_DURATION_SECONDS')}</Form.Label>
                        <Form.Control required type="number" min="1" value={form.minDurationSeconds} onChange={updateField('minDurationSeconds')}/>
                      </Form.Group>
                    </div>
                  </div>
                  <Form.Group>
                    <Form.Label style={labelTextStyle}>{t('ADMIN_AI_USER_INSTRUCTIONS')}</Form.Label>
                    <Form.Control as="textarea" rows={3} value={form.userInstructions} onChange={updateField('userInstructions')}/>
                  </Form.Group>
                  <div className="d-flex align-items-center flex-wrap" style={{gap: 10}}>
                    <Button type="submit" className="btn btn_red_rp" disabled={saving || !token}>
                      {saving ? <Spinner animation="border" size="sm" className="mr-2"/> : null}
                      {form.id ? t('SAVE_CHANGES') : t('SAVE_CONFIGURATION')}
                    </Button>
                    {form.id ? <Button type="button" variant="outline-secondary" onClick={resetForm}>{t('CANCEL_EDIT')}</Button> : null}
                  </div>
                </Form>
                {loading ? (
                  <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="mr-2"/>
                    {t('LOADING')}
                  </div>
                ) : (
                  <Table responsive bordered hover style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={whiteTextStyle}>{t('SPORT')}</th>
                        <th style={whiteTextStyle}>{t('PROVIDER')}</th>
                        <th style={whiteTextStyle}>{t('STATE')}</th>
                        <th style={whiteTextStyle}>{t('ADMIN_AI_USER_PRICE')}</th>
                        <th style={whiteTextStyle}>Euro/min</th>
                        <th style={whiteTextStyle}>{t('ADMIN_AI_PROVIDER_COST')}</th>
                        <th style={whiteTextStyle}>Euro/min</th>
                        <th style={whiteTextStyle}>{t('MAX_DURATION')}</th>
                        <th style={whiteTextStyle}>{t('MIN_DURATION')}</th>
                        <th/>
                      </tr>
                    </thead>
                    <tbody>
                      {configs.map(config => (
                        <tr key={config.id}>
                          <td style={whiteTextStyle}>{config.sport}</td>
                          <td style={whiteTextStyle}>{config.provider}</td>
                          <td style={whiteTextStyle}>{config.enabled ? t('ENABLED') : t('DISABLED')}</td>
                          <td style={whiteTextStyle}>{config.pricePerMinuteCents} cent/min</td>
                          <td style={whiteTextStyle}>{centsToEur(config.pricePerMinuteCents)} / min</td>
                          <td style={whiteTextStyle}>{config.providerCostPerMinuteCents} cent/min</td>
                          <td style={whiteTextStyle}>{centsToEur(config.providerCostPerMinuteCents)} / min</td>
                          <td style={whiteTextStyle}>{Math.floor((Number(config.maxDurationSeconds) || 0) / 60)} min</td>
                          <td style={whiteTextStyle}>{Number(config.minDurationSeconds) || 0} sec</td>
                          <td><Button size="sm" variant="light" onClick={() => editConfig(config)}>{t('EDIT')}</Button></td>
                        </tr>
                      ))}
                      {configs.length === 0 ? (
                        <tr>
                          <td colSpan="10" style={whiteTextStyle}>{t('ADMIN_AI_NO_CONFIGS')}</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </Container>
      </Fade>
    </Fragment>
  );
};

AdminAi.propTypes = {
  token: PropTypes.string
};

AdminAi.defaultProps = {
  token: null
};

export default connect(({login: {token}}) => ({token}))(AdminAi);
